<template>
  <v-accordion-item
    :heading="app.use_existing ? $t('examples.createApp.useYourApp') : $t('examples.createApp.createAnApp')"
  >
    <p v-if="app.use_existing" v-html="app.use_existing" />
    <i18n-t
      v-else-if="type === 'messages' || type === 'dispatch'"
      tag="p"
      keypath="examples.createApp.messagesOrDispatch.noApplication"
      scope="global"
    >
      <template #createOneLink>
        <link-wrapper href="/messages/code-snippets/create-an-application">{{
          $t('examples.createApp.messagesOrDispatch.createOne')
        }}</link-wrapper>
      </template>
      <template #configureYourWebhooksLink>
        <link-wrapper href="/messages/code-snippets/configure-webhooks">{{
          $t('examples.createApp.messagesOrDispatch.configureYourWebhooks')
        }}</link-wrapper>
      </template>
    </i18n-t>
    <div v-else>
      <i18n-t keypath="examples.createApp.applicationExplanation" tag="p" scope="global">
        <template #vonageCliLink>
          <link-wrapper href="https://github.com/vonage/vonage-cli" target="_blank" rel="noopener">{{
            $t('examples.createApp.vonageCli')
          }}</link-wrapper>
        </template>
        <template #viaTheDashboardLink>
          <link-wrapper href="${CUSTOMER_DASHBOARD_URL}/voice/create-application" target="_blank" rel="noopener">{{
            $t('examples.createApp.viaTheDashboard')
          }}</link-wrapper>
        </template>
        <template #seeOurVonageConceptsGuideLink>
          <link-wrapper href="/application/overview">{{
            $t('examples.createApp.seeOurVonageConceptsGuide')
          }}</link-wrapper>
        </template>
      </i18n-t>
      <Heading tag="h4">{{ $t('examples.createApp.installTheCli') }}</Heading>
      <TermynalSnippet>
        <span data-ty="input">npm install -g nexmo-cli</span>
      </TermynalSnippet>

      <Heading tag="h4">{{ $t('examples.createApp.createAnApp') }}</Heading>
      <i18n-t keypath="examples.createApp.onceYouHaveTheCliInstalled" tag="p" scope="global">
        <template #nexmoApplicationId>
          <code>NEXMO_APPLICATION_ID</code>
        </template>
        <template #privateKey>
          <code>private.key</code>
        </template>
        <template #initializeYourDependencies>
          <em>{{ $t('examples.createApp.initializeYourDependencies') }}</em>
        </template>
      </i18n-t>

      <i18n-t v-if="!app.disable_ngrok" keypath="examples.createApp.vonageNeedsToConnect" tag="p" scope="global">
        <template #answerUrl>
          <code>answer_url</code>
        </template>
        <template #ngrokLink>
          <link-wrapper href="/blog/2017/07/04/local-development-nexmo-ngrok-tunnel-dr/">ngrok</link-wrapper>
        </template>
        <template #demoNgrokUrl>
          <code>demo.ngrok.io</code>
        </template>
      </i18n-t>
      <TermynalSnippet>
        <span data-ty="input"
          >nexmo app:create "{{ name }}" {{ answerUrl }} {{ eventUrl }} --keyfile private.key{{
            type === 'rtc' ? ' --type=rtc' : ''
          }}</span
        >
      </TermynalSnippet>

      <div v-if="type === 'rtc'">
        <Heading tag="h4">{{ $t('examples.createApp.rtc.useYourExistingApp') }}</Heading>
        <p>{{ $t('examples.createApp.rtc.useAnAppToAuthenticate') }}</p>
        <i18n-t tag="p" keypath="examples.createApp.rtc.seeTheDocs" scope="global">
          <template #conversationApiDocsLink>
            <link-wrapper href="/conversation/overview">{{
              $t('examples.createApp.rtc.conversationApiDocs')
            }}</link-wrapper>
          </template>
        </i18n-t>
        <i18n-t tag="p" keypath="examples.createApp.rtc.alternativelyYouCanReview" scope="global">
          <template #applicationTutorialLink>
            <link-wrapper href="/client-sdk/setup/create-your-application">{{
              $t('examples.createApp.rtc.applicationTutorial')
            }}</link-wrapper>
          </template>
        </i18n-t>
      </div>
    </div>
  </v-accordion-item>
</template>
<script lang="ts" setup>
import { computed, PropType } from 'vue';
import { Application } from '@bifrost-dev-docs-mfe/common/types/examples';
import { VAccordionItem } from '@vonage/vivid-vue';
import TermynalSnippet from '@/components/markdown/widgets/TermynalSnippet.vue';
import { LinkWrapper } from '@/components/markdown/widgets';
import Heading from '@/components/example/components/Heading.vue';

const props = defineProps({
  app: { type: Object as PropType<Application>, required: true },
});

/**
 * FIXME: We should remove this default once we're sure that all Code Snippets have a type set e.g audit
 */
const type = computed(() => props.app.type || 'voice');

const name = computed(() => props.app.name || 'Example Project');

const NGROK_URL = 'http://demo.ngrok.io';
const EXAMPLE_URL = 'https://example.com';
const baseUrl = computed(() => (props.app.disable_ngrok ? EXAMPLE_URL : NGROK_URL));
const eventUrl = computed(() => props.app.event_url || `${baseUrl.value}/webhooks/events`);
const answerUrl = computed(() => props.app.answer_url || `${baseUrl.value}/webhooks/answer`);
</script>
